import { useState } from 'react'

import '../css/post-survey.scss'

/**
 * Generic survey modal component.
 * 
 * Supported question types:
 *   scale           — numbered buttons with endpoint labels (e.g., 1-5)
 *   radio           — horizontal button group with text options
 *   multiple_choice — vertical lettered options (A, B, C, D) for quiz-style questions
 *   text            — free text input
 * 
 * Additional fields:
 *   subtitle  — small text below the label
 *   optional  — if true, not required for submission
 *   showIf    — { key, value } conditional rendering based on another answer
 */
export default function SurveyModal({ title, subtitle, questions, onSubmit }) {
  const [responses, setResponses] = useState({})
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Filter questions based on showIf conditions
  const visibleQuestions = questions.filter(q => {
    if (!q.showIf) return true
    return responses[q.showIf.key] === q.showIf.value
  })

  const handleSelect = (key, value) => {
    setResponses(prev => ({ ...prev, [key]: value }))
    setError('')
  }

  const handleSubmit = async () => {
    // Validate only visible, required questions
    const unanswered = visibleQuestions
      .filter(q => !q.optional)
      .find(q => responses[q.key] === undefined || responses[q.key] === '')

    if (unanswered) {
      setError('Please answer all questions before continuing.')
      return
    }

    setSubmitting(true)
    try {
      await onSubmit(responses)
    } catch (err) {
      console.error('Survey submission failed:', err)
      setError('Failed to submit. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const renderQuestion = (question, index) => {
    switch (question.type) {
      case 'scale':
        return renderScale(question, index)
      case 'radio':
        return renderRadio(question, index)
      case 'multiple_choice':
        return renderMultipleChoice(question, index)
      case 'text':
        return renderText(question, index)
      default:
        return null
    }
  }

  const renderScale = (question, index) => {
    const min = question.min || 1
    const max = question.max || 5
    const values = Array.from({ length: max - min + 1 }, (_, i) => min + i)

    return (
      <div className="survey-question" key={question.key}>
        <label>{index + 1}. {question.label}</label>
        {question.subtitle && (
          <span className="survey-subtitle">{question.subtitle}</span>
        )}
        <div className="survey-options-row">
          <span className="survey-anchor">{question.minLabel || min}</span>
          <div className="survey-options">
            {values.map(val => (
              <button
                key={val}
                className={`survey-option ${responses[question.key] === val ? 'selected' : ''}`}
                onClick={() => handleSelect(question.key, val)}
              >
                {val}
              </button>
            ))}
          </div>
          <span className="survey-anchor">{question.maxLabel || max}</span>
        </div>
      </div>
    )
  }

  const renderRadio = (question, index) => {
    return (
      <div className="survey-question" key={question.key}>
        <label>{index + 1}. {question.label}</label>
        {question.subtitle && (
          <span className="survey-subtitle">{question.subtitle}</span>
        )}
        <div className="survey-radio-group">
          {question.options.map(option => (
            <button
              key={option}
              className={`survey-radio-option ${responses[question.key] === option ? 'selected' : ''}`}
              onClick={() => handleSelect(question.key, option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    )
  }

  const renderMultipleChoice = (question, index) => {
    const letters = ['A', 'B', 'C', 'D', 'E', 'F']
    const isSelected = (option) => responses[question.key] === option

    return (
      <div className="survey-question" key={question.key}>
        <label>{index + 1}. {question.label}</label>
        {question.subtitle && (
          <span className="survey-subtitle">{question.subtitle}</span>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
          {question.options.map((option, i) => (
            <button
              key={option}
              onClick={() => handleSelect(question.key, option)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 14px',
                borderRadius: '8px',
                border: isSelected(option) ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                background: isSelected(option) ? '#eff6ff' : '#fafafa',
                cursor: 'pointer',
                fontSize: '13.5px',
                color: isSelected(option) ? '#1d4ed8' : '#374151',
                fontWeight: isSelected(option) ? 500 : 400,
                textAlign: 'left',
                transition: 'all 0.15s ease',
                width: '100%',
                fontFamily: 'inherit'
              }}
            >
              <span style={{
                width: '26px',
                height: '26px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 600,
                flexShrink: 0,
                background: isSelected(option) ? '#3b82f6' : '#e5e7eb',
                color: isSelected(option) ? '#fff' : '#6b7280',
              }}>
                {letters[i]}
              </span>
              {option}
            </button>
          ))}
        </div>
      </div>
    )
  }

  const renderText = (question, index) => {
    return (
      <div className="survey-question" key={question.key}>
        <label>{index + 1}. {question.label}</label>
        {question.subtitle && (
          <span className="survey-subtitle">{question.subtitle}</span>
        )}
        <input
          type="text"
          className="survey-text-input"
          placeholder={question.placeholder || ''}
          value={responses[question.key] || ''}
          onChange={(e) => handleSelect(question.key, e.target.value)}
        />
      </div>
    )
  }

  return (
    <div className="survey-overlay">
      <div className="survey-modal">
        <div className="survey-header">
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>

        <div className="survey-questions">
          {visibleQuestions.map((question, index) => renderQuestion(question, index))}
        </div>

        {error && <div className="survey-error">{error}</div>}

        <div className="survey-footer">
          <button
            className="survey-submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  )
}